import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const TOUR_STEPS = [
  {
    target: '[data-testid="dashboard-page"]',
    title: 'Welcome to Kairera!',
    description: 'This is your career intelligence command center. Let us show you around so you can get the most out of the platform.',
    position: 'center',
    highlight: false,
  },
  {
    target: '[data-testid="profile-completeness-card"]',
    title: 'Start with Your Profile',
    description: 'Upload your resume to build your career foundation. This unlocks ATS scoring and personalizes your entire experience.',
    position: 'bottom',
    highlight: true,
  },
  {
    target: '[data-testid="start-discovery-cta"], [data-testid="continue-ci-cta"]',
    title: 'Career Intelligence Engine',
    description: 'Take the 16-question discovery assessment. This maps your decision patterns across 12 career dimensions and unlocks your Career Persona.',
    position: 'bottom',
    highlight: true,
  },
  {
    target: '[data-testid="action-intelligence-dashboard"]',
    title: 'Intelligence Dashboard',
    description: 'View your career intelligence cards, track your grades, and see your overall progress.',
    position: 'right',
    highlight: true,
  },
  {
    target: '[data-testid="action-resume-studio"]',
    title: 'Resume Studio',
    description: 'Build and export professional resumes with 4 templates. Get instant ATS compatibility scores.',
    position: 'right',
    highlight: true,
  },
  {
    target: '[data-testid="action-community"]',
    title: 'Community Hub',
    description: 'Connect with other professionals, share career insights, and learn from peers.',
    position: 'right',
    highlight: true,
  },
  {
    target: '[data-testid="dashboard-page"]',
    title: 'You\'re All Set!',
    description: 'Start by uploading your resume or jumping into the Career Discovery. Every action earns XP toward your next level. Good luck!',
    position: 'center',
    highlight: false,
  },
];

export default function OnboardingTutorial({ onComplete }) {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const overlayRef = useRef(null);

  const currentStep = TOUR_STEPS[step];

  const measureTarget = useCallback(() => {
    if (!currentStep?.highlight) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      // Use viewport-relative coords (fixed overlay)
      setTargetRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Re-measure after scroll settles
      setTimeout(() => {
        const r2 = el.getBoundingClientRect();
        setTargetRect({
          top: r2.top - 8,
          left: r2.left - 8,
          width: r2.width + 16,
          height: r2.height + 16,
        });
      }, 400);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    return () => window.removeEventListener('resize', measureTarget);
  }, [measureTarget, step]);

  const next = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else finish();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const finish = () => {
    localStorage.setItem('kairera_tutorial_done', 'true');
    onComplete();
  };

  const isCenter = currentStep?.position === 'center';
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  const getTooltipStyle = () => {
    if (isCenter || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 60,
      };
    }
    const pos = currentStep.position;
    const base = { position: 'fixed', zIndex: 60 };
    if (pos === 'bottom') {
      return { ...base, top: `${targetRect.top + targetRect.height + 16}px`, left: `${targetRect.left}px`, maxWidth: `${Math.min(targetRect.width, 380)}px` };
    }
    if (pos === 'right') {
      return { ...base, top: `${targetRect.top}px`, left: `${targetRect.left + targetRect.width + 16}px`, maxWidth: '340px' };
    }
    return { ...base, top: `${targetRect.top - 16}px`, left: `${targetRect.left}px`, maxWidth: '380px' };
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={overlayRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50"
        data-testid="onboarding-tutorial"
        style={{ pointerEvents: 'auto' }}>

        {/* Overlay SVG with cutout */}
        <svg className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 45 }}>
          <defs>
            <mask id="tutorial-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetRect && targetRect.width > 0 && targetRect.height > 0 && (
                <motion.rect
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, x: targetRect.left, y: targetRect.top, width: targetRect.width, height: targetRect.height }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  rx="12" fill="black" />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#tutorial-mask)" />
        </svg>

        {/* Highlight ring */}
        {targetRect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed rounded-xl pointer-events-none"
            style={{
              top: `${targetRect.top}px`,
              left: `${targetRect.left}px`,
              width: `${targetRect.width}px`,
              height: `${targetRect.height}px`,
              boxShadow: '0 0 0 3px rgba(13,148,136,0.6), 0 0 20px rgba(13,148,136,0.3)',
              zIndex: 55,
            }} />
        )}

        {/* Tooltip */}
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          style={getTooltipStyle()}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid={`tutorial-step-${step}`}>

          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <motion.div
              className="h-full"
              style={{ background: '#0D9488' }}
              initial={{ width: 0 }}
              animate={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(13,148,136,0.1)' }}>
                  <Sparkles className="w-4 h-4" style={{ color: '#0D9488' }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold" style={{ color: '#0D1E3A' }}>{currentStep.title}</h3>
                  <p className="text-[9px] font-medium" style={{ color: '#94a3b8' }}>
                    Step {step + 1} of {TOUR_STEPS.length}
                  </p>
                </div>
              </div>
              <button onClick={finish} className="p-1 rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="tutorial-skip-btn">
                <X className="w-4 h-4" style={{ color: '#94a3b8' }} />
              </button>
            </div>

            <p className="text-[13px] leading-relaxed mb-4" style={{ color: '#64748b' }}>
              {currentStep.description}
            </p>

            <div className="flex items-center justify-between">
              <button onClick={finish}
                className="text-[11px] font-semibold" style={{ color: '#94a3b8' }}
                data-testid="tutorial-skip-all-btn">
                Skip Tour
              </button>
              <div className="flex gap-2">
                {!isFirst && (
                  <Button onClick={prev} className="h-8 px-3 text-xs font-semibold rounded-lg"
                    style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}
                    data-testid="tutorial-prev-btn">
                    <ChevronLeft className="w-3 h-3 mr-1" /> Back
                  </Button>
                )}
                <Button onClick={next} className="h-8 px-4 text-xs font-semibold rounded-lg"
                  style={{ background: '#0D9488', color: '#fff' }}
                  data-testid="tutorial-next-btn">
                  {isLast ? 'Get Started' : <>Next <ChevronRight className="w-3 h-3 ml-1" /></>}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
